import { useState, useRef, useLayoutEffect, useMemo, useCallback } from 'react';
import { v4 as uuidV4 } from 'uuid';
import { useLatest } from 'ahooks';
import { useTranslate } from '@tolgee/react';
import { DatePicker } from 'components/DatePicker';
import dayjs, { Dayjs } from 'lib/dayjs';

const FOREVER = dayjs('2999-12-12');

type EndTimeForeverPickerProps = {
  value?: [Dayjs, Dayjs] | null;
  onChange?: (val: [string, string]) => void;
  noYearMode?: boolean; // 不显示年份
  canNotSelectEnd?: boolean; // 可以不选择截止时间
  noForever?: boolean; // 不显示永久
  showTime?: boolean; //显示时间
};

function EndTimeForeverPicker(props: EndTimeForeverPickerProps) {
  const {
    noYearMode = false,
    canNotSelectEnd = false,
    noForever = false,
    value,
    onChange,
    showTime = false,
  } = props;
  const { t } = useTranslate();
  const onChangeRef = useLatest(onChange);
  const format = useCallback(
    (current: any) => {
      return current.isSame(FOREVER)
        ? '永久'
        : noYearMode
        ? current.format(`MM-DD ${showTime ? 'HH:mm' : ''}`)
        : current.format(`YYYY-MM-DD ${showTime ? 'HH:mm' : ''}`);
    },
    [noYearMode, showTime],
  );

  const pickerRef = useRef(null);

  const [id] = useState(() => {
    return uuidV4();
  });

  const [showRange, setShowRange] = useState(!noForever);
  const [start, setStart] = useState<Dayjs>(dayjs());
  const ranges = useMemo(() => {
    return showRange
      ? {
          永久: [start, FOREVER],
        }
      : (undefined as any as Record<any, any>);
  }, [showRange, start]);

  useLayoutEffect(() => {
    if (noForever) return;
    const picker = document.querySelector(`.i-want-to-observe-this-picker-${id}`);
    if (!picker) return;
    const start = picker.querySelectorAll('.ant-picker-input')?.[0];
    if (!start) return;
    const observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const classList = start.getAttribute('class')?.split(' ') || [];
          if (classList.includes('ant-picker-input-active')) {
            setShowRange(false);
          } else {
            // focus 结束日期
            const value = start.querySelector('input')?.value;
            value && setStart(dayjs(value));
            setShowRange(true);
          }
        }
      }
    });

    observer.observe(start, {
      attributes: true,
    });

    // eslint-disable-next-line consistent-return
    return () => {
      observer.disconnect();
    };
  }, [id, noForever]);

  const handleChange = (value: [string, string]) => {
    onChangeRef.current?.(value);
  };

  const canNotSelectEndProps = canNotSelectEnd
    ? {
        placeholder: [t('multiTimeSelect.不填代表永久生效'), ''] as [string, string],
      }
    : {};

  useLayoutEffect(() => {
    const picker = document.querySelector(`.i-want-to-observe-this-picker-${id}`);
    if (picker) {
      picker.title = t('multiTimeSelect.不填代表永久生效');
    }
  }, [id, t]);

  return (
    <DatePicker.RangePicker
      key={id}
      ref={pickerRef}
      allowEmpty={[false, canNotSelectEnd]}
      defaultValue={value}
      className={`i-want-to-observe-this-picker-${id}`}
      showTime={showTime ? { format: 'HH:mm' } : false}
      style={{ width: '100%' }}
      format={format}
      mode={['date', 'date']}
      onChange={(_, formatStr) => handleChange(formatStr)}
      ranges={ranges}
      {...canNotSelectEndProps}
    />
  );
}

export default EndTimeForeverPicker;
